import { observable, action } from 'mobx';
import { IInstance } from '../../../interfaces/IInstance';
import { IMessageAnalytics } from '../../../interfaces/IMessageAnalytics';
import { IMobileDevicesAnalytics } from '../../../interfaces/IMobileDevicesAnalytics';
import { IUser } from '../../../interfaces/IUser';

export class AnalyticsStore {

  ////////////////////////////////// Component properties //////////////////////////////////

  @observable public tenant: string;
  @observable public instance: IInstance;
  @observable public token: string;
  @observable public environment: "Development" | "Test" | "Production";
  @observable public highlightColor: string;
  @observable public user: IUser;

  /////////////////////////////////////// Instances ////////////////////////////////////////

  @observable public instances: IInstance[];
  @observable public instanceFilterOptions: any[];
  @observable public selectedInstance: string = "";

  ////////////////////////////////////// Categories ////////////////////////////////////////

  @observable public categories: string[];
  @observable public categoryFilterOptions: any[];
  @observable public selectedCategory: string = "";

  ////////////////////////////////////// Global State /////////////////////////////////////////////

  @observable public loading: boolean;
  @observable public loadingCommentsAndLikes: boolean;
  @observable public source: "total" | "web" | "mobile" | "android" | "ios" = "total";
  
  ///////////////////////////////////// Messages Tab //////////////////////////////////
  
  @observable public messagesRows: IMessageAnalytics[];
  @observable public messagesFromDate: Date = new Date(new Date().getTime() - (7 * 24 * 60 * 60 * 1000));
  @observable public messagesToDate: Date = new Date();
  @observable public messagesPeriodeFilter: "today" | "latestWeek" | "latestMonth" | "customPeriode" = "latestWeek";
  @observable public messagesResult: IMessageAnalytics[];
  @observable public messagesSortProperty: "views" | "uniqueViews" | "title" | "startDate" | "commentCount" | "likeCount" = "uniqueViews";
  @observable public topViewsOnMessage: IMessageAnalytics;
  @observable public topUniqueViewsOnMessage: IMessageAnalytics;
  @observable public topLikesOnMessage: IMessageAnalytics;
  @observable public topCommentsOnMessage: IMessageAnalytics;
  @observable public totalUniqueMobileViews: number;
  @observable public totalUniqueWebViews: number;
  @observable public messagesPublishedInPeriod: number;

  ////////////////////////////////// Mobile Devices Tab /////////////////////////////

  @observable public mobileDevicesRows: IMobileDevicesAnalytics[];
  @observable public mobileDevicesFromDate: Date = new Date(new Date().getTime() - (7 * 24 * 60 * 60 * 1000));
  @observable public mobileDevicesToDate: Date = new Date();
  @observable public mobileDevicesPeriodeFilter: "today" | "latestWeek" | "latestMonth" | "customPeriode" = "latestWeek";
  @observable public mobileDevicesResult: IMobileDevicesAnalytics[];
  @observable public mobileDevicesSortProperty: "email" | "devices" = "email";
  @observable public totalMobileDevicesRegistered: number;
  @observable public totalUserRegistrations: number;
  @observable public totalUserUnRegistrations: number;

  @action public sortMessages(sortProperty?: "views" | "uniqueViews" | "title" | "startDate" | "commentCount" | "likeCount"): IMessageAnalytics[] {
      sortProperty = sortProperty !== undefined ? sortProperty : this.messagesSortProperty;
      const messages = [...this.messagesResult];
      let result = messages.sort((a, b) => {
        let keyA;
        let keyB;
        switch (sortProperty) {
          case "views":
            keyA = a.views;
            keyB = b.views;
            break;
          case "uniqueViews":
            keyA = a.uniqueViews;
            keyB = b.uniqueViews;
            break;
          case "title":
            keyA = a.title;
            keyB = b.title;
            break;
          case "startDate":
            keyA = a.startDate;
            keyB = b.startDate;
            break;
          case "commentCount":
            keyA = a.commentCount ?? 0;
            keyB = b.commentCount ?? 0;
            break;          
          case "likeCount":
            keyA = a.likeCount ?? 0;
            keyB = b.likeCount ?? 0;
            break;
        }
        if (keyA < keyB) {
          return -1;
        }
        if (keyA > keyB) {
          return 1;
        }
        return 0;
      });
      if (this.messagesSortProperty !== "title") {
        result = result.reverse();
      }
      return result;
  }

  @action public sortMobileDevices(sortProperty?: "email" | "devices" | "startDate"): IMobileDevicesAnalytics[] {
    sortProperty = sortProperty !== undefined ? sortProperty : this.mobileDevicesSortProperty;
    let result = this.mobileDevicesResult.sort((a, b) => {
      let keyA;
      let keyB;
      switch (sortProperty) {
        case "email":
          keyA = a.email;
          keyB = b.email;
          break;
        case "devices":
          keyA = a.devices;
          keyB = b.devices;
          break;
      }
      if (keyA < keyB) {
        return -1;
      }
      if (keyA > keyB) {
        return 1;
      }
      return 0;
    });
    if (this.mobileDevicesSortProperty !== "email") {
      result = result.reverse();
    }
    return result;
  }
}